import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, Mail, Star, Crown, Package, Flag } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import ItemCard from "@/components/ItemCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function PublicProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reason, setReason] = useState("");

  useEffect(() => {
    api.get(`/users/${id}`).then(({ data }) => setData(data)).catch(() => toast.error("Utente non trovato"));
    api.get(`/reviews/${id}`).then(({ data }) => setReviews(data));
  }, [id]);

  const report = async () => {
    try { await api.post("/reports", { target_type: "user", target_id: id, reason }); toast.success("Segnalazione inviata"); setReason(""); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!data) return <Skeleton className="h-64 rounded-2xl max-w-3xl mx-auto" />;
  const u = data.user;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="rounded-2xl bg-card border border-border/60 tactile-shadow p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start">
        <Avatar className="h-20 w-20 ring-4 ring-primary/10"><AvatarImage src={mediaUrl(u.avatar_url)} /><AvatarFallback className="bg-primary/10 text-primary text-xl font-bold">{u.name?.slice(0,2).toUpperCase()}</AvatarFallback></Avatar>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-head font-extrabold text-2xl sm:text-3xl">{u.name}</h1>
            {u.plan === "premium" && <Badge className="bg-[hsl(var(--gold))] text-white border-0"><Crown className="h-3 w-3 mr-1" /> Premium</Badge>}
          </div>
          {u.bio && <p className="text-muted-foreground mt-2">{u.bio}</p>}
          <div className="flex flex-wrap gap-2 mt-3">
            {u.email_verified && <Badge className="bg-success text-white border-0"><Mail className="h-3 w-3 mr-1" /> Email verificata</Badge>}
            {u.identity_verified && <Badge className="bg-success text-white border-0"><ShieldCheck className="h-3 w-3 mr-1" /> Identità verificata</Badge>}
            {u.pickups_completed >= 3 && (u.no_shows || 0) === 0 && <Badge className="bg-[hsl(var(--gold))] text-white border-0">✓ Affidabile</Badge>}
          </div>
          <div className="flex items-center gap-4 mt-4 text-sm">
            <span className="flex items-center gap-1"><Star className="h-4 w-4 text-[hsl(var(--gold))] fill-[hsl(var(--gold))]" /> {u.rating_avg || "—"} ({u.rating_count})</span>
            <span className="flex items-center gap-1"><Package className="h-4 w-4 text-primary" /> {u.donations_count} donazioni</span>
          </div>
        </div>
        <Dialog>
          <DialogTrigger asChild><Button variant="ghost" size="sm" className="rounded-full text-muted-foreground" data-testid="report-user-btn"><Flag className="h-4 w-4" /></Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Segnala utente</DialogTitle></DialogHeader>
            <Textarea value={reason} onChange={(e)=>setReason(e.target.value)} placeholder="Motivo..." className="rounded-xl" data-testid="report-user-reason" />
            <DialogFooter><Button onClick={report} disabled={!reason} className="rounded-full">Invia</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <h2 className="font-head font-bold text-xl mt-8 mb-4">Annunci di {u.name}</h2>
      {data.listings.length === 0 ? <p className="text-muted-foreground">Nessun annuncio attivo.</p> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{data.listings.map((l,i)=><ItemCard key={l.id} item={l} index={i} />)}</div>
      )}

      {reviews.length > 0 && (
        <>
          <h2 className="font-head font-bold text-xl mt-10 mb-4">Recensioni</h2>
          <div className="grid gap-3">
            {reviews.map((r)=>(
              <div key={r.id} className="rounded-2xl border border-border/60 p-4">
                <div className="flex items-center justify-between"><span className="font-semibold">{r.reviewer_name}</span>
                  <span className="flex gap-0.5">{Array.from({length:5}).map((_,i)=><Star key={i} className={`h-4 w-4 ${i<r.rating?"text-[hsl(var(--gold))] fill-[hsl(var(--gold))]":"text-border"}`} />)}</span></div>
                {r.comment && <p className="text-sm text-muted-foreground mt-2">{r.comment}</p>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
